import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, ClipboardPaste, Sparkles, X } from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import { todayISO } from '../lib/dates'
import { askAi, aiFailureText, type AiFailure } from '../lib/aiClient'
import {
  ENQUIRY_SYSTEM, MAX_PASTE_CHARS,
  buildEnquiryPrompt, draftIsEmpty, parseEnquiryAnswer,
  type EnquiryDraft, type EnquiryField,
} from '../lib/enquiryDraft'
import { Button } from '../ui/kit'

/**
 * „Anfrage einfügen" — aus einer Nachricht wird ein Buchungsentwurf.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM DIESE FLÄCHE IM NEUEN-BUCHUNG-BLATT SITZT UND NIRGENDWO SONST.
 *
 * Der Vermieter hat die Anfrage im Mailprogramm oder in WhatsApp offen und will
 * eine Buchung daraus machen. Er drückt auf „+". Das Blatt, das dann aufgeht,
 * fragt ihn nach genau den Angaben, die in der Nachricht stehen: Anreise,
 * Abreise, wie viele Personen. Bisher tippte er sie ab.
 *
 * Deshalb steht der Knopf hier ganz oben — unmittelbar über den Feldern, die er
 * füllt, auf dem Weg, den der Vermieter ohnehin geht. Eine eigene Seite
 * „Anfragen" wäre ein zweiter Weg, eine Buchung anzulegen (mit eigenem
 * Gastanlegen, eigener Verfügbarkeitsprüfung, eigenen Fehlern) — und sie wäre
 * das, was in diesem Projekt schon mehrfach passiert ist: eine Funktion, die
 * niemand findet, weil sie nicht dort liegt, wo die Arbeit stattfindet.
 *
 * WAS HIER NICHT PASSIERT: speichern. Die eingefügte Nachricht ist eine
 * Gastnachricht und damit personenbezogen. Sie steht in einem `useState` dieses
 * Bauteils, geht einmal an das Modell und ist mit dem Schließen des Blattes weg
 * — keine Datenbank, kein localStorage, kein Protokoll. Das ist der Grund,
 * warum der Text nirgends „für später" gemerkt wird, obwohl das bequem wäre.
 *
 * UND: das Ergebnis ist ein ENTWURF. Diese Fläche schreibt nichts. Sie füllt
 * Formularfelder, die der Vermieter danach sieht, ändert und selbst speichert.
 * ═══════════════════════════════════════════════════════════════════════════
 */

type Phase =
  | { k: 'closed' }
  | { k: 'paste' }
  | { k: 'busy' }
  | { k: 'error'; failure: AiFailure }
  | { k: 'result'; draft: EnquiryDraft; notice: string | null }

export default function EnquiryPasteCard({ onApply }: {
  /** Bekommt den geprüften Entwurf. Gespeichert wird dadurch nichts. */
  onApply: (draft: EnquiryDraft) => void
}) {
  const { t, lang, formatDate } = useI18n()
  const de = lang === 'de'
  const store = useStore()
  const navigate = useNavigate()

  /** Die Gastnachricht. Lebt hier und nirgends sonst — siehe Kopf der Datei. */
  const [text, setText] = useState('')
  const [phase, setPhase] = useState<Phase>({ k: 'closed' })

  const reset = () => { setText(''); setPhase({ k: 'closed' }) }

  const run = async () => {
    const message = text.trim()
    if (!message) return
    setPhase({ k: 'busy' })

    const today = todayISO()
    const res = await askAi({
      purpose: 'enquiry_parse',
      system: ENQUIRY_SYSTEM,
      prompt: buildEnquiryPrompt(message, today),
      maxTokens: 700,
      live: store.live,
      de,
    })

    if (!res.ok) { setPhase({ k: 'error', failure: res.failure }); return }

    /*
     * Woran die Zitate geprüft werden.
     *
     * Im Normalfall am eingefügten Text: ein Beleg, der dort nicht vorkommt, ist
     * erfunden. In der Demo antwortet gar kein Modell — `askAi` liefert das
     * mitgelieferte Beispiel, das mit dem eingefügten Text nichts zu tun hat.
     * Dort ist die Antwort selbst der Ursprung, und die Beschriftung daneben
     * sagt dem Interessenten genau das.
     */
    const draft = parseEnquiryAnswer(res.text, {
      today,
      source: res.demo ? res.text : message,
    })
    setPhase({ k: 'result', draft, notice: res.notice })
  }

  // Freie Gastnachrichten können ausgeschlossene Daten enthalten. Solange keine
  // verlässliche lokale Strukturierung vorliegt, bleiben sie vor jedem KI-Aufruf.
  if (store.live) return (
    <div className="rounded-2xl border border-border bg-surface-2 px-3 py-2.5 text-xs text-muted">
      {de
        ? 'KI-Auslesen von Gastnachrichten ist derzeit pausiert. Bitte tragen Sie Datum, Personen und Gastdaten unten direkt ein.'
        : 'AI extraction of guest messages is currently paused. Please enter dates, guests and contact details directly below.'}
    </div>
  )

  /* ── zu: nur der Knopf ────────────────────────────────────────────────── */

  if (phase.k === 'closed') {
    return (
      <button
        type="button"
        onClick={() => setPhase({ k: 'paste' })}
        className="w-full text-left rounded-2xl border border-dashed border-border
                   bg-surface-2 px-3 py-2.5 flex items-center gap-3 active:bg-surface"
      >
        <ClipboardPaste size={18} className="text-accent shrink-0" />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium">
            {de ? 'Anfrage einfügen' : 'Paste an enquiry'}
          </span>
          <span className="block text-xs text-muted">
            {de
              ? 'Text aus Mail oder WhatsApp — Datum und Personen werden ausgelesen.'
              : 'Text from mail or WhatsApp — dates and guests are read out of it.'}
          </span>
        </span>
        <Sparkles size={15} className="text-muted shrink-0" />
      </button>
    )
  }

  /* ── offen ────────────────────────────────────────────────────────────── */

  return (
    <div className="rounded-2xl border border-border bg-surface-2 p-3 space-y-3">
      <div className="flex items-center gap-2">
        <ClipboardPaste size={16} className="text-accent shrink-0" />
        <span className="text-sm font-medium flex-1">
          {de ? 'Anfrage einfügen' : 'Paste an enquiry'}
        </span>
        <Button variant="ghost" size="sm" onClick={reset}
          aria-label={t('common.close')}>
          <X size={16} />
        </Button>
      </div>

      {(phase.k === 'paste' || phase.k === 'busy' || phase.k === 'error') && (
        <>
          <textarea
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, MAX_PASTE_CHARS))}
            rows={5}
            disabled={phase.k === 'busy'}
            placeholder={de
              ? 'Nachricht des Gastes hier einfügen …'
              : 'Paste the guest’s message here …'}
            className="w-full min-w-0 text-sm bg-surface border border-border rounded-xl
                       px-3 py-2.5 leading-relaxed disabled:opacity-60
                       focus:outline-2 focus:outline-accent"
          />

          {phase.k === 'error' && <Failure failure={phase.failure} de={de} navigate={navigate} />}

          <div className="flex gap-2">
            <Button
              variant="primary" size="sm" className="flex-1"
              disabled={!text.trim() || phase.k === 'busy'}
              onClick={() => void run()}
            >
              <Sparkles size={15} />
              {phase.k === 'busy'
                ? (de ? 'Wird gelesen …' : 'Reading …')
                : (de ? 'Angaben auslesen' : 'Read the details')}
            </Button>
          </div>

          {/* Die Zusage, die zu dieser Fläche gehört: der Text geht einmal an die
              KI und ist danach weg. Sie steht hier und nicht in der
              Datenschutzerklärung, weil sie hier gebraucht wird. */}
          <p className="text-[0.7rem] text-muted">
            {de
              ? 'Der eingefügte Text wird nicht gespeichert. Er wird einmal ausgewertet und verschwindet mit diesem Blatt.'
              : 'The pasted text is not stored. It is read once and disappears with this sheet.'}
          </p>
        </>
      )}

      {phase.k === 'result' && (
        <Result
          draft={phase.draft}
          notice={phase.notice}
          de={de}
          formatDate={formatDate}
          onDiscard={reset}
          onApply={() => { onApply(phase.draft); reset() }}
          onAgain={() => setPhase({ k: 'paste' })}
        />
      )}
    </div>
  )
}

/* ────────────────────────────────────────────────────────── Der Fehlerfall ──── */

/**
 * Kein Fehlercode, kein „non-2xx status code" — nur der Satz und, wo es einen
 * gibt, der Weg heraus. Die Übersetzung macht `aiFailureText`, damit an allen
 * KI-Flächen dasselbe steht.
 */
function Failure({ failure, de, navigate }: {
  failure: AiFailure
  de: boolean
  navigate: (to: string) => void
}) {
  const f = aiFailureText(failure, de)
  return (
    <div className="rounded-xl bg-warn-soft text-warn px-3 py-2.5 space-y-1.5">
      <div className="flex items-start gap-2">
        <AlertTriangle size={15} className="shrink-0 mt-0.5" />
        <div className="min-w-0">
          <div className="text-sm font-medium">{f.title}</div>
          <div className="text-xs leading-relaxed">{f.body}</div>
        </div>
      </div>
      {f.action && (
        <Button size="sm" variant="secondary" className="w-full"
          onClick={() => navigate(f.action!.to)}>
          {f.action.label}
        </Button>
      )}
    </div>
  )
}

/* ───────────────────────────────────────────────────────── Das Ergebnis ──── */

interface Row {
  field: EnquiryField
  label: string
  value: string
}

/**
 * Was erkannt wurde, mit dem Satz daneben, aus dem es stammt.
 *
 * WARUM DIE ZITATE MITSTEHEN: der Vermieter soll prüfen können, ohne die
 * Originalnachricht danebenzulegen. „Anreise 25.08.2026" allein zwingt ihn
 * zurück ins Mailprogramm; „Anreise 25.08.2026 — «wir würden gerne vom 25.8.
 * kommen»" beantwortet die Frage an Ort und Stelle.
 *
 * WARUM AUCH DAS FEHLENDE DASTEHT: eine Lücke, die man nicht sieht, füllt man
 * nicht. Das Modell lässt absichtlich leer, was es nicht sicher liest — dieser
 * Satz macht aus dem Schweigen eine Ansage.
 */
function Result({ draft, notice, de, formatDate, onApply, onDiscard, onAgain }: {
  draft: EnquiryDraft
  notice: string | null
  de: boolean
  formatDate: (iso: string) => string
  onApply: () => void
  onDiscard: () => void
  onAgain: () => void
}) {
  const label: Record<EnquiryField, string> = {
    arrival: de ? 'Anreise' : 'Arrival',
    departure: de ? 'Abreise' : 'Departure',
    adults: de ? 'Erwachsene' : 'Adults',
    children: de ? 'Kinder' : 'Children',
    childrenAges: de ? 'Alter der Kinder' : 'Children’s ages',
    firstName: de ? 'Vorname' : 'First name',
    lastName: de ? 'Nachname' : 'Last name',
    email: 'E-Mail',
    phone: de ? 'Telefon' : 'Phone',
    wishes: de ? 'Wünsche' : 'Wishes',
    pet: de ? 'Haustier' : 'Pet',
  }

  const rows: Row[] = []
  const add = (field: EnquiryField, value: string | null) => {
    if (value) rows.push({ field, label: label[field], value })
  }
  add('arrival', draft.arrival && formatDate(draft.arrival))
  add('departure', draft.departure && formatDate(draft.departure))
  add('adults', draft.adults !== null ? String(draft.adults) : null)
  add('children', draft.children !== null ? String(draft.children) : null)
  add('childrenAges', draft.childrenAges.length
    ? draft.childrenAges.join(de ? ' und ' : ' and ') + (de ? ' Jahre' : ' years')
    : null)
  add('firstName', draft.firstName)
  add('lastName', draft.lastName)
  add('email', draft.email)
  add('phone', draft.phone)
  add('wishes', draft.wishes)
  add('pet', draft.pet === null
    ? null
    : draft.pet ? (de ? 'ja' : 'yes') : (de ? 'nein' : 'no'))

  /* Was das Modell offengelassen hat — beim Namen genügt eine der beiden Hälften. */
  const missing: string[] = []
  if (!draft.arrival) missing.push(label.arrival)
  if (!draft.departure) missing.push(label.departure)
  if (draft.adults === null) missing.push(label.adults)
  if (!draft.firstName && !draft.lastName) missing.push(de ? 'Name' : 'Name')

  if (draftIsEmpty(draft)) {
    return (
      <div className="space-y-3">
        <p className="text-sm bg-warn-soft text-warn rounded-xl px-3 py-2">
          {de
            ? 'Aus diesem Text ließ sich nichts sicher herauslesen. Bitte die Angaben von Hand eintragen — oder einen Text einfügen, in dem Zeitraum und Personenzahl stehen.'
            : 'Nothing could be read from this text with confidence. Please enter the details by hand — or paste a text that names the dates and the number of guests.'}
        </p>
        {notice && <p className="text-[0.7rem] text-muted italic">{notice}</p>}
        <Button size="sm" variant="secondary" className="w-full" onClick={onAgain}>
          {de ? 'Anderen Text einfügen' : 'Paste a different text'}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted">
        {de
          ? 'Vorschlag — bitte prüfen. Nichts davon ist gespeichert.'
          : 'A suggestion — please check it. Nothing is saved yet.'}
      </p>

      <ul className="space-y-1.5">
        {rows.map((r) => (
          <li key={r.field} className="text-sm">
            <div className="flex items-baseline gap-2">
              <span className="text-xs text-muted w-28 shrink-0">{r.label}</span>
              <span className="min-w-0 flex-1 font-medium break-words">{r.value}</span>
            </div>
            {draft.quotes[r.field] && (
              <div className="pl-0 sm:pl-30 text-[0.7rem] text-muted italic break-words">
                «{draft.quotes[r.field]}»
              </div>
            )}
          </li>
        ))}
      </ul>

      {missing.length > 0 && (
        <p className="text-xs rounded-xl px-3 py-2 bg-surface text-muted">
          {de
            ? `Nicht erkannt und daher leer: ${missing.join(', ')}. Was nicht eindeutig im Text steht, wird nicht geraten.`
            : `Not recognised, so left empty: ${missing.join(', ')}. Anything not stated clearly is not guessed.`}
        </p>
      )}

      {notice && <p className="text-[0.7rem] text-muted italic">{notice}</p>}

      <div className="flex gap-2">
        <Button variant="primary" size="sm" className="flex-1" onClick={onApply}>
          {de ? 'In den Entwurf übernehmen' : 'Use for the draft'}
        </Button>
        <Button variant="ghost" size="sm" onClick={onDiscard}>
          {de ? 'Verwerfen' : 'Discard'}
        </Button>
      </div>
    </div>
  )
}
